<template>
    <div class="dash">
      <h1 class="topper">Local News</h1>

      <!-- Date Navigation -->
      <div class="date-navigation">
        <img src="@/assets/left arrow icon.svg" alt="Previous Date" class="nav-arrow" @click="previousDate" :class="{ disabled: isMinDate }"/>
        <span class="current-date">{{ visibleDate }}</span>
        <img src="@/assets/arrow icon.svg" alt="Next Date" class="nav-arrow" @click="nextDate" :class="{ disabled: isMaxDate }" />
      </div>

      <div class="article-container">
        <div v-if="loading" class="loading">Loading articles...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <div v-else-if="articlesWithSummary.length === 0" class="no-meetings">No published content that matches your filters.</div>
        <div v-for="article in articlesWithSummary" 
             :key="article.id" 
             class="article-card"
             :class="{ 'article-collapsed': isCollapsed(article.id) && shouldCollapse(article.id) }"
             ref="articleCards">
          <div class="article-content" @click="openArticle(article.url)">
            <p class="cardhead">{{ article.title }}</p>
            <p class="cardsubhead">{{ article.source}}&nbsp;&nbsp;•&nbsp;&nbsp;{{ article.town }}</p>  
            <p class="cardsummary">{{ article.displaySummary }}</p>
          </div>
          <div v-if="shouldCollapse(article.id)" 
               class="collapse-control"
               @click.stop="toggleCollapse(article.id)">
            <span class="caret" :class="{ 'caret-rotated': !isCollapsed(article.id) }">▼</span>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';

export default {
  name: 'LocalNewsDashboard',
  props: {
    settings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentDate: new Date(),
      articles: [],
      loading: false,
      error: null,
      collapsedArticles: new Set(),
      articleHeights: {}
    };
  },
  computed: {
    formattedDate() {
      const year = this.currentDate.getFullYear();
      const month = String(this.currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.currentDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    visibleDate() {
        const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
        const month = months[this.currentDate.getMonth()];
        const day = this.currentDate.getDate();
        const year = this.currentDate.getFullYear();
        return `${month} ${day}, ${year}`;
    },
    
    isMaxDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return this.currentDate >= today;
    },
    
    isMinDate() {
      return false;
    },
    
    collections() {
      const sources = [];
      if (this.settings.vtDigger) {
        sources.push({ name: 'DiggerArticles', source: 'VT Digger' });
      }
      if (this.settings.sevenDays) {
        sources.push({ name: 'SevenDaysArticles', source: 'Seven Days' });
      }
      if (this.settings.addisonIndependent) {
        sources.push({ name: 'AddiArticles', source: 'Addison Independent' });
      }
      if (this.settings.vtPublic) {
        sources.push({ name: 'vtpublicarticles', source: 'VT Public' });
      }
      if (this.settings.brandonReporter) {
        sources.push({ name: 'BrandonReporterArticles', source: 'Brandon Reporter' });
      }
      return sources;
    },
    
    articlesWithSummary() {
      return this.articles.map(article => ({
        ...article,
        displaySummary: this.settings.aiSummary ? article.aisummary : article.summary
      }));
    }
  },
  
  methods: {
    previousDate() {
      if (this.isMinDate) return;
      this.resetArticleState();
      this.currentDate = new Date(this.currentDate.setDate(this.currentDate.getDate() - 1));
    },  
    
    nextDate() {
        if (this.isMaxDate) return;
        this.resetArticleState();
        this.currentDate = new Date(this.currentDate.setDate(this.currentDate.getDate() + 1));
    },

    resetArticleState() {
      this.articles = [];
      this.collapsedArticles.clear();
      this.articleHeights = {};
    },

    shouldCollapse(articleId) {
      return this.articleHeights[articleId] > 250;
    },

    isCollapsed(articleId) {
      return this.collapsedArticles.has(articleId);
    },

    toggleCollapse(articleId) {
      if (this.collapsedArticles.has(articleId)) {
        this.collapsedArticles.delete(articleId);
      } else {
        this.collapsedArticles.add(articleId);
      }
    },

    updateArticleHeights() {
      this.articleHeights = {};
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.articleCards) {
            this.$refs.articleCards.forEach((card, index) => {
              const articleId = this.articlesWithSummary[index].id;
              this.articleHeights[articleId] = card.offsetHeight;
              
              // Only add to collapsed set if not already managed
              if (this.articleHeights[articleId] > 250 && !this.collapsedArticles.has(articleId)) {
                this.collapsedArticles.add(articleId);
              }
            });
          }
        }, 100);
      });
    },
    
    async fetchArticles() {
      this.loading = true;
      this.error = null;
      const allArticles = [];
      
      const excludedTags = ['obituary', 'By the way', 'opinion', 'commentary', 'obituaries', 'crossword', 'free will astrology', 'From the Publisher', 'Ask the Rev.', 'Legals'];
      const normalizedExcludedTags = excludedTags.map(tag => tag.trim().toLowerCase());
      // console.log('Current settings:', this.settings); // Log settings
      // console.log('Active collections:', this.collections); // Log which collections are being queried

      try {
        for (const col of this.collections) {
          const q = query(
            collection(db, col.name),
            where('usabledate', '==', this.formattedDate)
          );
          
          try {
            const querySnapshot = await getDocs(q);
            const articlesFromSource = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              source: col.source
            }));
            allArticles.push(...articlesFromSource);
          } catch (error) {
            console.error(`Error fetching from ${col.name}:`, error);
          }
        }
        
        this.articles = allArticles.filter(article => {
          if (article.tags) {
            const tagsArray = Array.isArray(article.tags)
              ? article.tags
              : article.tags.split(',');
              
            const normalizedTags = tagsArray.map(tag =>
              tag.trim().toLowerCase()
            );
            
            return !normalizedTags.some(tag => normalizedExcludedTags.includes(tag));
          }
          
          return true;
        });

        await this.$nextTick();
        this.updateArticleHeights();
      } catch (error) {
        this.error = error.message || 'Failed to fetch articles';
        console.error('Error in fetchArticles:', error);
      } finally {
        this.loading = false;
      }
    },
    
    openArticle(url) {
      window.open(url, '_blank');
    }
  },
  
  watch: {
    formattedDate: {
      handler: 'fetchArticles'
    },
    settings: {
      handler: 'fetchArticles',
      deep: true
    }
  },
  
  mounted() {
    this.fetchArticles();
    window.addEventListener('resize', this.updateArticleHeights);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateArticleHeights);
  }
};
</script>
  
<style scoped>
.dash {
  background-color: #fffff8;
  padding: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
  
.topper {
  text-align: center;
  padding-top: 64px;
  margin-bottom: 10px;
}
  
.date-navigation {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 0px;
}
  
.nav-arrow {
  width: 14px;
  height: 14px;
  cursor: pointer;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: content-box;
  color: #545051;
}
  
.nav-arrow.disabled {
  opacity: 0.2;
  cursor: not-allowed;
}
  
.current-date {
  font-size: 16px;
  font: inherit;
  color: #545051;
}
  
.article-container {
  width: 80%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
  
.article-card {
  width: 100%;
  margin-top: 20px;
  background-color: #fffff8;
  border: 1px solid #545051;
  border-radius: 20px;
  padding: 10px;
  position: relative;
  transition: max-height 0.3s ease-out;
  overflow: hidden;
  padding-bottom: 30px; 
}

.article-collapsed {
  max-height: 250px;
}

.article-content {
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.collapse-control {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0; 
  height: 30px;
  background:#fffff8;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.caret {
  color: #545051;
  font-size: 12px;
  transition: transform 0.3s ease;
}

.caret-rotated {
  transform: rotate(180deg);
}

.article-card:last-child {
  margin-bottom: 20px;
}
  
.article-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.cardhead {
  font: inherit;
  font-weight: 600;
  font-size: 14pt;
  color: #545051;
  margin: 0rem;
  padding-bottom: 0rem;
}

.cardsubhead {
  font-size: 1rem;
  font-family: 'IBM Plex Serif', serif;
  color: #545051;
  line-height: 16pt;
  padding-bottom: 12pt;
}
  
.cardsummary {
  font-size: 1rem;
  color: #545051;
  line-height: 16pt;
}

.loading,
.error,
.no-meetings {
  text-align: left;
  margin: 20px 20px;
  font: inherit;
  color: #545051;
}
  
.error {
  color: red;
}

@media (max-width: 767px) {
  .topper {
    display: none;
  }
}
</style>